import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../assets/css/divercommission.css";
import apiClient from "../../api/axios";
import SafeImage from "../../components/common/SafeImage";

export default function DriverCommissionRequests() {
  const navigate = useNavigate();
  const { driverId } = useParams();

  const [rows, setRows] = useState([]);
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewType, setViewType] = useState("table");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ✅ Accept Modal State
  const [acceptOpen, setAcceptOpen] = useState(false);

    // ✅ Reject Modal State
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [acceptAmount, setAcceptAmount] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // ✅ currency symbol state (from API)
  const [currencySymbol, setCurrencySymbol] = useState(" ");
  const [adminDescription, setAdminDescription] = useState("");

  // ✅ NEW: View Modal State
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  // ✅ NEW: Edit Modal State
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editReceiptNo, setEditReceiptNo] = useState("");
  const [editReceiptImage, setEditReceiptImage] = useState(null);
  const [editReceiptPreviewUrl, setEditReceiptPreviewUrl] = useState("");
  const [editAdminDescription, setEditAdminDescription] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // ✅ TOAST STATE
  const [toast, setToast] = useState({
    open: false,
    type: "info", // "success" | "error" | "warn" | "info"
    message: "",
  });
  const toastTimerRef = useRef(null);

  const showToast = (message, type = "info", ms = 2500) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);

    setToast({ open: true, type, message });

    toastTimerRef.current = setTimeout(() => {
      setToast((t) => ({ ...t, open: false }));
    }, ms);
  };

  const closeToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast((t) => ({ ...t, open: false }));
  };

  // cleanup toast timer
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (driverId) {
      fetchRequests();
    } else {
      setLoading(false);
      setError("Driver ID not found in URL");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  async function fetchRequests() {
    
    try {
      setLoading(true);
      setError("");

      const res = await apiClient.get(
        `/admin/drivers/${driverId}/commission-requests`,
      );
      const payload = res?.data;

      let list = [];
      // ✅ driver always from payload.driver (your response has it)
let driverInfo = payload?.driver || payload?.data?.driver || null;

      if (Array.isArray(payload?.data)) {
        list = payload.data;
      } else if (Array.isArray(payload?.data?.requests)) {
        list = payload.data.requests;
        driverInfo = payload.data.driver || null;
      } else {
        list = [];
      }

      const sym =
        (list?.[0]?.currency_symbol ||
          payload?.currency_symbol ||
          payload?.data?.currency_symbol ||
          " ") + "";
      setCurrencySymbol(sym);
      setRows(list);
      setDriver(driverInfo);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to load requests";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }

  const formatCurrency = (amount, symbol = currencySymbol) => {
    const num = Number(amount) || 0;
    return `${symbol} ${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getDriverSummary = () => {
    const totalRequests = rows.length;
    const pending = rows.filter(
      (r) =>
        String(r?.status || "")
          .toLowerCase()
          .trim() === "pending",
    ).length;
    const approved = rows.filter((r) => {
      const s = String(r?.status || "")
        .toLowerCase()
        .trim();
      return s === "approved" || s === "accepted";
    }).length;
    const rejected = rows.filter(
      (r) =>
        String(r?.status || "")
          .toLowerCase()
          .trim() === "rejected",
    ).length;
   const totalAmount = Number(rows?.[0]?.commission ?? 0);

    return { totalRequests, pending, approved, rejected, totalAmount };
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesSearch =
        !searchTerm ||
        (row?.order_id?.toString() || "").includes(searchTerm) ||
        (row?.message || "").toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        String(row?.status || "")
          .toLowerCase()
          .trim() === statusFilter.toLowerCase().trim();

      const matchesDate = true;
      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [rows, searchTerm, statusFilter]);

  const summary = getDriverSummary();

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
  };

  const StatusChip = ({ status }) => {
    const s = String(status || "")
      .toLowerCase()
      .trim();
    const config = {
      pending: { label: "Pending", className: "dcChip dcChipWarn", icon: <IcoClock/> },
      approved: { label: "Approved", className: "dcChip dcChipOk", icon: <IcoCheck/> },
      accepted: { label: "Accepted", className: "dcChip dcChipOk", icon: <IcoCheck/> },
      rejected: {
        label: "Rejected",
        className: "dcChip dcChipBad",
        icon: "❌",
      },
    };

    const chip = config[s] || {
      label: status || "Unknown",
      className: "dcChip dcChipNeutral",
      icon: "ℹ️",
    };

    return (
      <span className={chip.className}>
        <span className="dcChipIcon" aria-hidden="true">
          {chip.icon}
        </span>
        <span className="dcChipText">{chip.label}</span>
      </span>
    );
  };

  const SummaryCard = ({ title, value, tone, icon }) => (
    <div className={`dcSumCard ${tone}`}>
      <div className="dcSumTop">
        <div className="dcSumIcon" aria-hidden="true">
          {icon}
        </div>
        <div className="dcSumTitle">{title}</div>
      </div>
      <div className="dcSumValue">{value}</div>
      <div className="dcSumGlow" />
    </div>
  );

  // ✅ open accept modal
  const openAccept = (row) => {
    setSelectedRow(row);
    setAcceptAmount(String(row?.requested_amount ?? ""));
    setReceiptNo("");
    setReceiptImage(null);
    setAdminDescription("");
    setReceiptPreviewUrl("");
    setSaveError("");
    setAcceptOpen(true);

    if (row?.currency_symbol) setCurrencySymbol(String(row.currency_symbol));
  };

  const closeAccept = () => {
    if (saving) return;
    setAcceptOpen(false);
    setSelectedRow(null);
    setAcceptAmount("");
    setReceiptNo("");
    setReceiptImage(null);
    setReceiptPreviewUrl("");
    setSaveError("");
  };

  // ✅ open view modal (for accepted/approved rows)
  const openView = (row) => {
    setViewRow(row);
    setViewOpen(true);

    if (row?.currency_symbol) setCurrencySymbol(String(row.currency_symbol));
  };

  const closeView = () => {
    setViewOpen(false);
    setViewRow(null);
  };

  //helper: convert stored path to full URL
const receiptUrl = (path) => {
  if (!path) return "";
  if (String(path).startsWith("http")) return String(path);

  const BASE =
    import.meta.env.VITE_API_URL?.replace(/\/$/, "") || "http://localhost:8000";

  // path = "commission_receipts/xxx.jpg"
  return `${BASE}/${String(path).replace(/^\/+/, "")}`;
};

  // ✅ open edit modal (accepted/approved rows)
  const openEdit = (row) => {
    setEditRow(row);
    setEditAmount(String(row?.approved_amount ?? row?.paid_commission ?? ""));
    setEditReceiptNo(String(row?.receipt_no ?? ""));
    setEditReceiptImage(null);
    setEditAdminDescription(String(row?.admin_description ?? ""));
    setEditError("");
    setEditOpen(true);

    const url = row?.receipt_image ? receiptUrl(row.receipt_image) : "";
    setEditReceiptPreviewUrl(url);

    if (row?.currency_symbol) setCurrencySymbol(String(row.currency_symbol));
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditOpen(false);
    setEditRow(null);
    setEditAmount("");
    setEditReceiptNo("");
    setEditReceiptImage(null);
    setEditReceiptPreviewUrl("");
    setEditAdminDescription("");
    setEditError("");
  };

  // ✅ cleanup preview URLs (accept + edit)
  useEffect(() => {
    return () => {
      if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
      if (
        editReceiptPreviewUrl &&
        String(editReceiptPreviewUrl).startsWith("blob:")
      ) {
        URL.revokeObjectURL(editReceiptPreviewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveAccept() {
    try {
      if (!selectedRow?.order_id) {
        setSaveError("Order ID missing");
        showToast("Order ID missing", "error");
        return;
      }

      if (!acceptAmount || Number(acceptAmount) <= 0) {
        setSaveError("Please enter valid amount");
        showToast("Please enter valid amount", "warn");
        return;
      }

      if (!receiptNo.trim()) {
        setSaveError("Please enter receipt no");
        showToast("Please enter receipt no", "warn");
        return;
      }

      if (!receiptImage) {
        setSaveError("Please choose receipt image");
        showToast("Please choose receipt image", "warn");
        return;
      }

      setSaving(true);
      setSaveError("");

      const fd = new FormData();
      fd.append("driver_id", driverId); // backend may ignore (ok)
      fd.append("order_id", selectedRow.order_id);
      fd.append("paid_commission", acceptAmount);
      fd.append("receipt_no", receiptNo);
      fd.append("receipt_image", receiptImage);
      fd.append("description", adminDescription);

      await apiClient.post("/admin/driver-commission-requests/accept", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      closeAccept();
      fetchRequests();
      showToast("Accepted & Saved Successfully", "success");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to save accept";
      setSaveError(msg);
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit() {
    try {
      if (!editRow?.order_id) {
        setEditError("Order ID missing");
        showToast("Order ID missing", "error");
        return;
      }

      if (!editAmount || Number(editAmount) <= 0) {
        setEditError("Please enter valid amount");
        showToast("Please enter valid amount", "warn");
        return;
      }

      if (!editReceiptNo.trim()) {
        setEditError("Please enter receipt no");
        showToast("Please enter receipt no", "warn");
        return;
      }

      setEditSaving(true);
      setEditError("");

      const fd = new FormData();
      fd.append("driver_id", driverId);
      fd.append("order_id", editRow.order_id);
      fd.append("paid_commission", editAmount);
      fd.append("receipt_no", editReceiptNo);
      fd.append("description", editAdminDescription);

      if (editReceiptImage) {
        fd.append("receipt_image", editReceiptImage);
      }

      await apiClient.post("/admin/driver-commission-requests/update", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      closeEdit();
      fetchRequests();
      showToast("Updated Successfully", "success");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to update";
      setEditError(msg);
      showToast(msg, "error");
    } finally {
      setEditSaving(false);
    }
  }

 async function confirmReject() {
  try {
    if (!rejectRow?.order_id) {
      showToast("Order ID missing", "error");
      return;
    }

    setRejecting(true);

    await apiClient.post(`/admin/driver-commission-requests/reject`, {
      driver_id: driverId,
      order_id: rejectRow?.order_id,
      reason: rejectReason?.trim() || undefined,
    });

    setRejectOpen(false);
    setRejectRow(null);
    setRejectReason("");

    fetchRequests();
    showToast("Rejected Successfully", "success");
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      e?.message ||
      "Failed to reject";
    showToast(msg, "error");
  } finally {
    setRejecting(false);
  }
}

  if (!driverId) {
    return (
      <div className="dcOnly">
        <div className="dcPageWrap">
          <div className="dcCard">
            <div className="dcAlert dcAlertError">
              Driver ID is missing. Please navigate from the drivers list.
            </div>
            <button
              className="dcBtn dcBtnPrimary"
              onClick={() => navigate("/admin/drivers")}
            >
              ← Back to Drivers
            </button>
          </div>
        </div>

        {/* ✅ TOAST */}
        <div
          className={`dcToast ${
            toast.open ? "dcToastShow" : ""
          } dcToast-${toast.type}`}
          role="status"
          aria-live="polite"
        >
          <div className="dcToastMsg">{toast.message}</div>
          <button className="dcToastClose" onClick={closeToast} type="button">
            ✕
          </button>
        </div>
      </div>
    );
  }
const IcoCalendar = () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>;
const IcoClock    = () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>;
const IcoCheck    = () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const IcoCoin     = () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v2m0 8v2M9 9.5a3 3 0 0 1 6 0c0 1.5-1.5 2-3 2.5S9 13 9 15a3 3 0 0 0 6 0"/></svg>;
  return (
    <div className="dcOnly">
      <div className="dcPageWrap">
        {/* Header */}
        <div className="dcHero">
          <div className="dcHeroRow">
            <div className="dcHeroLeft">
              <div className="dcHeroTitleRow">
                <button
                  className="dcIconBtn"
                  onClick={() => navigate(-1)}
                  title="Back"
                >
                  ←
                </button>
                <div>
                  <div className="dcHeroTitle">Commission Requests</div>
                 <div className="dcHeroMeta">
                  {driver?.name ? (
                    <span className="dcHeroDriver">
                        <SafeImage
                        
                          src={driver?.profile_image}
                          alt={driver?.name}
                          className="dcHeroDriverImg"
                        />
                        <span className="dcPill">{driver.name}</span>
                    </span>
                    ) : null}
                      <span className="dcPill">Driver ID: {driverId}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: "none" }}>
              <button onClick={() => setViewType("table")}>table</button>
              <button onClick={() => setViewType("card")}>card</button>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="dcGrid4">
          <SummaryCard
            title="Total Requests"
            value={summary.totalRequests}
            tone="dcTone1"
            icon={<IcoCalendar/>}
          />
          <SummaryCard
            title="Pending"
            value={summary.pending}
            tone="dcTone2"
            icon={<IcoClock/>}
          />
          <SummaryCard
            title="Approved"
            value={summary.approved}
            tone="dcTone3"
            icon={<IcoCheck/>}
          />
          <SummaryCard
            title="Total Amount"
            value={formatCurrency(summary.totalAmount)}
            tone="dcTone4"
            icon={<IcoCoin/>}
          />
        </div>

        {/* Filters */}
        <div className="dcCard">
          <div className="dcFilters">
            <div className="dcField dcFieldGrow">
              <label className="dcLabel">Search</label>
              <input
                className="dcInput"
                type="text"
                placeholder="Order ID / message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="dcField">
              <label className="dcLabel">Status</label>
              <select
                className="dcInput"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Bar */}
        {!loading && !error && rows.length > 0 && (
          <div className="dcResultBar">
            <div className="dcProgress">
              <div
                className="dcProgressFill"
                style={{
                  width: `${
                    rows.length ? (filteredRows.length / rows.length) * 100 : 0
                  }%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="dcCard dcCenter">
            <div className="dcLoader" />
            <div className="dcMuted">Loading commission requests…</div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="dcCard">
            <div className="dcAlert dcAlertError">
              <span>{error}</span>
              <button
                className="dcBtn dcBtnDanger"
                onClick={fetchRequests}
                type="button"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
          <div className="dcCard dcCenter">
            <div className="dcEmptyIcon">🔎</div>
            <div className="dcEmptyTitle">No matching requests found</div>
            <div className="dcMuted">Try adjusting your search or filters.</div>
            <button
              className="dcBtn dcBtnGhost"
              onClick={clearFilters}
              type="button"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Table View */}
        {!loading &&
          !error &&
          filteredRows.length > 0 &&
          viewType === "table" && (
            <div className="dcCard">
              <div className="dcCardHead">
                <div className="dcCardHeadTitle">Commission Requests</div>
              </div>
          
              <div className="dcTableWrap">
                <table className="dcTable">
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "12%" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th className="dcRight">Commission</th>
                      <th className="dcRight">Paid Commission</th>
                      <th className="dcRight">Requested Amount</th>
                      <th className="dcRight">Balance</th>
                      <th>Status</th>
                      <th>Message</th>
                      <th className="dcRight dcThActions">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((row, index) => {
                      const st = String(row?.status || "")
                        .toLowerCase()
                        .trim();
                      const canAct = st === "pending";
                      const canView = st === "accepted" || st === "approved" ||st === "rejected";

                      const rowSymbol =
                        (row?.currency_symbol || currencySymbol || " ") + "";

                      return (
                        <tr
                          key={row?.id ?? `${row?.order_id ?? "row"}-${index}`}
                        >
                          <td>
                            <div className="dcStrong">
                              #{row?.order_id || "N/A"}
                            </div>
                          </td>

                          <td className="dcRight">
                            <div className="dcStrong">
                              {formatCurrency(row?.commission, rowSymbol)}
                            </div>
                          </td>

                          <td className="dcRight">
                            <div className="dcStrong">
                              {formatCurrency(row?.paid_commission, rowSymbol)}
                            </div>
                          </td>

                          <td className="dcRight">
                            <div className="dcStrong">
                              {formatCurrency(row?.requested_amount, rowSymbol)}
                            </div>
                           
                          </td>

                          <td className="dcRight">
                            <div className="dcStrong">
                              {formatCurrency(
                                row?.commission_balance,
                                rowSymbol,
                              )}
                            </div>
                          </td>

                          <td>
                            <StatusChip status={row?.status} />
                          </td>

                          <td className="dcMessage">
                            <span title={row?.message || "-"}>
                              {row?.message || "-"}
                            </span>
                          </td>

                          <td className="dcRight dcTdActions">
                            {canAct ? (
                              <div className="dcActions">
                                <button
                                  className="dcMiniBtn dcMiniBtnOk"
                                  type="button"
                                  onClick={() => openAccept(row)}
                                >
                                  Accept
                                </button>
                             <button
  className="dcMiniBtn dcMiniBtnBad"
  type="button"
  onClick={() => {
    setRejectRow(row);
    setRejectReason("");
    setRejectOpen(true);
  }}
>
  Reject
</button>
                              </div>
                            ) : canView ? (
                              <div className="dcActions">
                                <button
                                  className="dcMiniBtn dcMiniBtnNeutral"
                                  type="button"
                                  onClick={() => openView(row)}
                                >
                                  View
                                </button>
                                {(st === "accepted" || st === "approved") && (
                                <button
                                  className="dcMiniBtn dcMiniBtnOk"
                                  type="button"
                                  onClick={() => openEdit(row)}
                                >
                                  Edit
                                </button>
                                 )}
                              </div>
                            ) : (
                              <span className="dcMuted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        {/* ✅ ACCEPT MODAL */}
        {acceptOpen && (
          <div className="dcModalOverlay" onMouseDown={closeAccept}>
            <div className="dcModal" onMouseDown={(e) => e.stopPropagation()}>
              <div className="dcModalHead">
                <div>
                  <div className="dcModalTitle">Accept Commission Request</div>
                  <div className="dcModalSub dcMuted">
                    
                    Driver ID: <b>{driverId}</b> • Order ID:{" "}
                    <b>{selectedRow?.order_id}</b>
                  </div>
                </div>
                <button
                  className="dcIconBtn"
                  onClick={closeAccept}
                  disabled={saving}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {saveError ? (
                <div className="dcAlert dcAlertError" style={{ marginTop: 12 }}>
                  <span>{saveError}</span>
                </div>
              ) : null}

              <div className="dcModalBody">
                <div className="dcField">
                  <label className="dcLabel">Paid Commission</label>
                  <input
                    className="dcInput"
                    type="number"
                    placeholder="Enter amount"
                    value={acceptAmount}
                    onChange={(e) => setAcceptAmount(e.target.value)}
                  />
                </div>

                <div className="dcField">
                  <label className="dcLabel">Receipt No</label>
                  <input
                    className="dcInput"
                    type="text"
                    placeholder="Enter receipt no"
                    value={receiptNo}
                    onChange={(e) => setReceiptNo(e.target.value)}
                  />
                </div>

                <div className="dcField">
                  <label className="dcLabel">Receipt Image</label>

                  <div className="dcFileRow">
                    <label className="dcFileBtn">
                      Choose Image
                      <input
                        className="dcFileInputHidden"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setReceiptImage(f);

                          if (receiptPreviewUrl)
                            URL.revokeObjectURL(receiptPreviewUrl);
                          if (f) {
                            const url = URL.createObjectURL(f);
                            setReceiptPreviewUrl(url);
                          } else {
                            setReceiptPreviewUrl("");
                          }
                        }}
                      />
                    </label>

                    <div
                      className="dcFileName"
                      title={receiptImage?.name || ""}
                    >
                      {receiptImage ? (
                        <>
                          Selected: <b>{receiptImage.name}</b>
                        </>
                      ) : (
                        <span className="dcMuted">No file chosen</span>
                      )}
                    </div>
                  </div>

                  {receiptPreviewUrl ? (
                    <div className="dcPreviewBox">
                      <SafeImage
                        src={receiptPreviewUrl}
                        alt="Receipt Preview"
                        className="dcReceiptPreviewImg"
                        onError={() => {
                          setSaveError(
                            "Preview failed. Please choose a valid image.",
                          );
                          showToast(
                            "Preview failed. Please choose a valid image.",
                            "error",
                          );
                        }}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="dcField">
                  <label className="dcLabel">Description</label>
                  <textarea
                    className="dcInput"
                    rows={3}
                    placeholder="Enter description..."
                    value={adminDescription}
                    onChange={(e) => setAdminDescription(e.target.value)}
                  />
                </div>
              </div>

              <div className="dcModalActions">
                <button
                  className="dcBtn dcBtnGhost"
                  onClick={closeAccept}
                  disabled={saving}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="dcBtn dcBtnPrimary"
                  onClick={saveAccept}
                  disabled={saving}
                  type="button"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ EDIT MODAL (Accepted rows) */}
        {editOpen && editRow && (
          <div className="dcModalOverlay" onMouseDown={closeEdit}>
            <div className="dcModal" onMouseDown={(e) => e.stopPropagation()}>
              <div className="dcModalHead">
                <div>
                  <div className="dcModalTitle">Edit Commission Details</div>
                  <div className="dcModalSub dcMuted">
                     {driver?.name ? (
                        <div className="dcModalDriverRow">
                          <SafeImage
                            src={driver?.profile_image}
                            alt={driver?.name}
                            className="dcModalDriverImg"
                          />
                          <div>
                            <div className="dcModalDriverName">{driver.name}</div>
                          
                          </div>
                        </div>
                      ) : null}
                    Driver ID: <b>{driverId}</b> • Order ID:{" "}
                    <b>{editRow?.order_id}</b>
                  </div>
                </div>
                <button
                  className="dcIconBtn"
                  onClick={closeEdit}
                  disabled={editSaving}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {editError ? (
                <div className="dcAlert dcAlertError" style={{ marginTop: 12 }}>
                  <span>{editError}</span>
                </div>
              ) : null}

              <div className="dcModalBody">
                <div className="dcField">
                  <label className="dcLabel">Approved / Paid Amount</label>
                  <input
                    className="dcInput"
                    type="number"
                    placeholder="Enter amount"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                  />
                </div>

                <div className="dcField">
                  <label className="dcLabel">Receipt No</label>
                  <input
                    className="dcInput"
                    type="text"
                    placeholder="Enter receipt no"
                    value={editReceiptNo}
                    onChange={(e) => setEditReceiptNo(e.target.value)}
                  />
                </div>

                <div className="dcField">
                  <label className="dcLabel">Receipt Image</label>

                  <div className="dcFileRow">
                    <label className="dcFileBtn">
                      Change Image
                      <input
                        className="dcFileInputHidden"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setEditReceiptImage(f);

                          if (f) {
                            const url = URL.createObjectURL(f);
                            setEditReceiptPreviewUrl(url);
                          }
                        }}
                      />
                    </label>

                    <div className="dcFileName">
                      {editReceiptImage ? (
                        <>
                          Selected: <b>{editReceiptImage.name}</b>
                        </>
                      ) : (
                        <span className="dcMuted">Keep existing image</span>
                      )}
                    </div>
                  </div>

                  {editReceiptPreviewUrl ? (
                    <div className="dcPreviewBox">
                      <SafeImage
                        src={editReceiptPreviewUrl}
                        alt="Receipt Preview"
                        className="dcReceiptPreviewImg"
                        onError={() => {
                          setEditError("Receipt preview failed");
                          showToast("Receipt preview failed", "error");
                        }}
                      />
                    </div>
                  ) : (
                    <div className="dcMuted" style={{ marginTop: 8 }}>
                      No image available
                    </div>
                  )}
                </div>

                <div className="dcField">
                  <label className="dcLabel">Admin Description</label>
                  <textarea
                    className="dcInput"
                    rows={3}
                    placeholder="Enter description..."
                    value={editAdminDescription}
                    onChange={(e) => setEditAdminDescription(e.target.value)}
                  />
                </div>
              </div>

              <div className="dcModalActions">
                <button
                  className="dcBtn dcBtnGhost"
                  onClick={closeEdit}
                  disabled={editSaving}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="dcBtn dcBtnPrimary"
                  onClick={saveEdit}
                  disabled={editSaving}
                  type="button"
                >
                  {editSaving ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </div>
        )}
    {/* ✅ REJECT MODAL */}
{/* ✅ REJECT MODAL (NO INPUTS) */}
{rejectOpen && rejectRow && (
  <div
    className="dcModalOverlay"
    onMouseDown={() => {
      if (rejecting) return;
      setRejectOpen(false);
      setRejectRow(null);
    }}
  >
    <div
      className="dcModal dcRejectModal2"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="dcModalHead">
        <div>
          <div className="dcModalTitle">Reject Commission Request</div>
          <div className="dcModalSub dcMuted">
            Driver ID: <b>{driverId}</b> • Order ID: <b>{rejectRow?.order_id}</b>
          </div>
        </div>

        <button
          className="dcIconBtn"
          type="button"
          title="Close"
          disabled={rejecting}
          onClick={() => {
            if (rejecting) return;
            setRejectOpen(false);
            setRejectRow(null);
          }}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="dcModalBody">
        {/* Driver row */}
        <div className="dcRejectDriverRow">
          <SafeImage
            src={driver?.profile_image}
            alt={driver?.name || "Driver"}
            className="dcRejectDriverImg"
          />
          <div className="dcRejectDriverInfo">
            <div className="dcRejectDriverName">
              {driver?.name || "Driver"}
            </div>
            <div className="dcMuted">Order #{rejectRow?.order_id}</div>
          </div>
        </div>

        {/* Amounts */}
        <div className="dcRejectAmountGrid">
          <div className="dcRejectAmtCard">
            <div className="dcRejectAmtLabel">Commission</div>
            <div className="dcRejectAmtValue">
              {formatCurrency(
                rejectRow?.commission,
                rejectRow?.currency_symbol || currencySymbol,
              )}
            </div>
          </div>

          <div className="dcRejectAmtCard">
            <div className="dcRejectAmtLabel">Requested Amount</div>
            <div className="dcRejectAmtValue">
              {formatCurrency(
                rejectRow?.requested_amount,
                rejectRow?.currency_symbol || currencySymbol,
              )}
            </div>
          </div>

          <div className="dcRejectAmtCard">
            <div className="dcRejectAmtLabel">Balance</div>
            <div className="dcRejectAmtValue">
              {formatCurrency(
                rejectRow?.commission_balance,
                rejectRow?.currency_symbol || currencySymbol,
              )}
            </div>
          </div>
        </div>

        {/* Warning note */}
        <div className="dcRejectWarn">
          ⚠️ This action will mark the request as <b>Rejected</b>.
        </div>
      </div>

      {/* Footer actions */}
      <div className="dcModalActions dcRejectActions2">
        <button
          className="dcBtn dcBtnGhost"
          type="button"
          disabled={rejecting}
          onClick={() => {
            setRejectOpen(false);
            setRejectRow(null);
          }}
        >
          Cancel
        </button>

        <button
          className="dcBtn dcBtnDanger"
          type="button"
          disabled={rejecting}
          onClick={confirmReject}
        >
          {rejecting ? "Rejecting..." : "Reject"}
        </button>
      </div>
    </div>
  </div>
)}
        {/* ✅ VIEW MODAL (Accepted rows) */}
        {viewOpen && viewRow && (
          <div className="commission-modal-overlay" onMouseDown={closeView}>
            <div
              className="commission-modal"
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="commission-modal-header">
                <div className="commission-header-content">
                  <div className="commission-title-section">
                    <div className="commission-header-title">
                      <div className="commission-main-title">
                        Commission Request
                      </div>
                      <div className="commission-subtitle">
                        Detailed Information
                      </div>
                    </div>
                    {driver?.name ? (
                      <div className="dcModalDriverRow">
                        <SafeImage
                          src={driver?.profile_image}
                          alt={driver?.name}
                          className="dcModalDriverImg"
                        />
                        <div>
                          <div className="dcModalDriverName">{driver.name}</div>
                        
                        </div>
                      </div>
                    ) : null}
                    <div className="commission-identifiers">
                      <div className="commission-id-item">
                        <span className="commission-id-label">Driver ID:</span>
                        <span className="commission-id-value">{driverId}</span>
                      </div>
                      <div className="commission-id-item">
                        <span className="commission-id-label">Order ID:</span>
                        <span className="commission-id-value">
                          {viewRow?.order_id}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="commission-close-btn"
                    onClick={closeView}
                    aria-label="Close modal"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M15 5L5 15M5 5L15 15" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="commission-modal-body">
                {/* Overview Section */}
                <div className="commission-section">
                  <div className="commission-section-title">
                    <div className="section-title-text">Overview</div>
                    <div className="section-title-border"></div>
                  </div>

                  <div className="commission-overview-grid">
                    {/* Status Card */}
                    <div className="commission-card">
                      <div className="card-header">
                        <div className="card-icon">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M10 1C5.03 1 1 5.03 1 10C1 14.97 5.03 19 10 19C14.97 19 19 14.97 19 10C19 5.03 14.97 1 10 1ZM10 17.5C5.86 17.5 2.5 14.14 2.5 10C2.5 5.86 5.86 2.5 10 2.5C14.14 2.5 17.5 5.86 17.5 10C17.5 14.14 14.14 17.5 10 17.5Z"
                              fill="#4B5563"
                            />
                            <path
                              d="M13.5 6.5L9 11L6.5 8.5L5.5 9.5L9 13L14.5 7.5L13.5 6.5Z"
                              fill="#4B5563"
                            />
                          </svg>
                        </div>
                        <div className="card-title">Status</div>
                      </div>
                      <div className="card-content">
                        <div className="status-display">
                          <StatusChip status={viewRow?.status} />
                        </div>
                      </div>
                    </div>

                    {/* Timestamp Card */}
                    <div className="commission-card">
                      <div className="card-header">
                        <div className="card-icon">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M10 1C5.03 1 1 5.03 1 10C1 14.97 5.03 19 10 19C14.97 19 19 14.97 19 10C19 5.03 14.97 1 10 1ZM10 17.5C5.86 17.5 2.5 14.14 2.5 10C2.5 5.86 5.86 2.5 10 2.5C14.14 2.5 17.5 5.86 17.5 10C17.5 14.14 14.14 17.5 10 17.5Z"
                              fill="#4B5563"
                            />
                            <path
                              d="M10.5 5H9V10.5L13.75 13.15L14.5 11.92L10.5 9.75V5Z"
                              fill="#4B5563"
                            />
                          </svg>
                        </div>
                        <div className="card-title">Request Time</div>
                      </div>
                      <div className="card-content">
                        <div className="timestamp-display">
                          <div className="date-display">
                            {formatDate(viewRow?.created_at)}
                          </div>
                          <div className="time-display">
                            {formatTime(viewRow?.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Message Card */}
                    <div className="commission-card message-card">
                      <div className="card-header">
                        <div className="card-icon">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M18 2H2C0.9 2 0 2.9 0 4V18C0 19.1 0.9 20 2 20H18C19.1 20 20 19.1 20 18V4C20 2.9 19.1 2 18 2ZM18 18H2V6L10 11L18 6V18ZM10 9L2 4H18L10 9Z"
                              fill="#4B5563"
                            />
                          </svg>
                        </div>
                        <div className="card-title">Driver Message</div>
                      </div>
                      <div className="card-content">
                        <div className="message-content">
                          {viewRow?.message ? (
                            <div className="message-text">
                              {viewRow.message}
                            </div>
                          ) : (
                            <div className="empty-message">
                              No message provided
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Section */}
                <div className="commission-section">
                  <div className="commission-section-title">
                    <div className="section-title-text">Financial Details</div>
                    <div className="section-title-border"></div>
                  </div>

                  <div className="commission-financial-grid">
                    <div className="financial-card">
                      <div className="financial-icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 2L2 7L12 12L22 7L12 2Z"
                            stroke="#111827"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2 17L12 22L22 17"
                            stroke="#111827"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2 12L12 17L22 12"
                            stroke="#111827"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="financial-label">Commission</div>
                      <div className="financial-amount">
                        {formatCurrency(
                          viewRow?.commission,
                          viewRow?.currency_symbol || currencySymbol,
                        )}
                      </div>
                      <div className="financial-description">
                        Original amount
                      </div>
                    </div>

                    <div className="financial-card requested-card">
                      <div className="financial-icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 8V12L15 15"
                            stroke="#D97706"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            stroke="#D97706"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                      <div className="financial-label">Requested</div>
                      <div className="financial-amount requested-amount">
                        {formatCurrency(
                          viewRow?.requested_amount,
                          viewRow?.currency_symbol || currencySymbol,
                        )}
                      </div>
                      <div className="financial-description">
                        Driver's request
                      </div>
                    </div>

                    <div className="financial-card approved-card">
                      <div className="financial-icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M9 12L11 14L15 10"
                            stroke="#059669"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            stroke="#059669"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                      <div className="financial-label">Approved</div>
                      <div className="financial-amount approved-amount">
                        {formatCurrency(
                          viewRow?.approved_amount ??
                            viewRow?.paid_commission ??
                            0,
                          viewRow?.currency_symbol || currencySymbol,
                        )}
                      </div>
                      <div className="financial-description">
                        Amount approved
                      </div>
                    </div>
                  </div>
                </div>

                {/* Documentation Section */}
                <div className="commission-section">
                  <div className="commission-section-title">
                    <div className="section-title-text">Documentation</div>
                    <div className="section-title-border"></div>
                  </div>

                  <div className="commission-documentation-grid">
                    {/* Receipt Info */}
                    <div className="commission-document-card">
                      <div className="document-card-header">
                        <div className="document-card-title">
                          Receipt Information
                        </div>
                      </div>
                      <div className="document-card-content">
                        <div className="document-item">
                          <div className="document-label">Receipt Number</div>
                          <div className="document-value">
                            {viewRow?.receipt_no ? (
                              <span className="receipt-number">
                                {viewRow.receipt_no}
                              </span>
                            ) : (
                              <span className="empty-value">
                                - Not provided -
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="document-item">
                          <div className="document-label">Balance</div>
                          <div className="document-value balance-amount">
                            {formatCurrency(
                              viewRow?.commission_balance,
                              viewRow?.currency_symbol || currencySymbol,
                            )}
                          </div>
                          <div className="document-hint">
                            Remaining commission balance
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Receipt Image */}
                    <div className="commission-document-card">
                      <div className="document-card-header">
                        <div className="document-card-title">Receipt Image</div>
                      </div>
                      <div className="document-card-content">
                        <div className="receipt-image-container">
                          {viewRow?.receipt_image ? (
                            <>
                              <div className="receipt-preview-wrapper">
                                <SafeImage
                                  src={receiptUrl(viewRow.receipt_image)}
                                  alt="Receipt"
                                  className="receipt-preview-image"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    if (e.currentTarget.parentElement) {
                                      e.currentTarget.parentElement.innerHTML = `
                                      <div class="receipt-error">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                          <path d="M12 9V11M12 15H12.01M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        <div>Failed to load image</div>
                                      </div>
                                    `;
                                    }
                                    showToast(
                                      "Failed to load receipt image",
                                      "error",
                                    );
                                  }}
                                />
                              </div>
                              <div className="receipt-actions">
                                <a
                                  href={receiptUrl(viewRow.receipt_image)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="receipt-action-btn"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                  >
                                    <path
                                      d="M8 11V3"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M5 6L8 3L11 6"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M14 10V14H2V10"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                  Open in New Tab
                                </a>
                              </div>
                            </>
                          ) : (
                            <div className="empty-receipt">
                              <div className="empty-receipt-icon">
                                <svg
                                  width="56"
                                  height="56"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z"
                                    stroke="#9CA3AF"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M14 2V8H20"
                                    stroke="#9CA3AF"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M16 13H8"
                                    stroke="#9CA3AF"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M16 17H8"
                                    stroke="#9CA3AF"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M10 9H9H8"
                                    stroke="#9CA3AF"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </div>
                              <div className="empty-receipt-text">
                                No receipt uploaded
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Admin Notes */}
                    <div className="commission-document-card admin-notes-card">
                      <div className="document-card-header">
                        <div className="document-card-title">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9 15C5.685 15 3 12.315 3 9C3 5.685 5.685 3 9 3C12.315 3 15 5.685 15 9C15 12.315 12.315 15 9 15Z"
                              fill="#4B5563"
                            />
                            <path
                              d="M9.75 5.25H8.25V9.75L12 11.25L12.75 10.125L9.75 8.625V5.25Z"
                              fill="#4B5563"
                            />
                          </svg>
                          Admin Notes
                        </div>
                      </div>
                      <div className="document-card-content">
                        <div className="admin-notes-content">
                          {viewRow?.admin_description ? (
                            <div className="notes-text">
                              {viewRow.admin_description}
                            </div>
                          ) : (
                            <div className="empty-notes">
                              No admin notes available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="commission-modal-footer">
                <div className="commission-footer-content">
                  <button
                    className="commission-close-primary"
                    onClick={closeView}
                  >
                    Close Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {!loading && !error && (
          <div className="dcFooter">
            <div className="dcMuted">
              Copyright © {new Date().getFullYear()} Go Home Express
            </div>
          </div>
        )}
      </div>

      {/* ✅ TOAST */}
      <div
        className={`dcToast ${
          toast.open ? "dcToastShow" : ""
        } dcToast-${toast.type}`}
        role="status"
        aria-live="polite"
      >
        <div className="dcToastMsg">{toast.message}</div>
        <button className="dcToastClose" onClick={closeToast} type="button">
          ✕
        </button>
      </div>
    </div>
  );
}
